import { useQuery } from '@tanstack/react-query';
import { Form, Input, InputNumber, Select, Switch } from 'antd';
import { useState } from 'react';

import { apiFetch } from '@/api/client';
import { DocumentTypeValues, type DocumentType } from '@/api/generated/enums';
import { useBranches, useFinancialYears } from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { FormDrawer } from '@/components/FormDrawer';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { enumOptions, humanizeEnum } from '@core/format/text';
import type { Session } from '@core/session/types';
import { formatDocumentNumber, nextDocumentNumber } from './documentNumber';

interface NumberingSeries {
  id: number;
  branchId: number;
  financialYearId: number;
  documentType: DocumentType;
  prefix: string;
  includeFinancialYear: boolean;
  paddingWidth: number;
  separator: string;
  currentNumber: number;
  isActive: boolean;
}

const seriesKey = ['document-numbering-series'] as const;

/**
 * Numbering series configuration (spec Section 15).
 *
 * One counter per branch x financial year x document type, which is why the list is filtered by
 * the first two rather than showing every combination at once — a company with four branches and
 * three open years would otherwise be reading a hundred rows to find one.
 *
 * There is deliberately **no "issue next number" button** here, although the endpoint exists. That
 * call permanently consumes a number whether or not a document is ever saved against it; a gap in
 * the sequence is an accounting curiosity but an unexplained one invites questions at audit. The
 * number is issued by the document that needs it, and nowhere else. What this screen shows instead
 * is what the next number *would be*, computed locally.
 */
export function DocumentNumberingPage({ session }: { session: Session }) {
  const branches = useBranches();
  const years = useFinancialYears();

  const [branchId, setBranchId] = useState<number | undefined>(session.branch?.id);
  const [financialYearId, setFinancialYearId] = useState<number | undefined>(undefined);
  const [creating, setCreating] = useState(false);

  // Default to the current year once the list arrives, without overriding a choice already made.
  const currentYear = years.data?.find((year) => year.isCurrent);
  const activeYearId = financialYearId ?? currentYear?.id;
  const activeBranchId = branchId ?? branches.data?.[0]?.id;

  const yearCode = years.data?.find((year) => year.id === activeYearId)?.code ?? null;

  const series = useQuery({
    queryKey: [...seriesKey, { branchId: activeBranchId, financialYearId: activeYearId }],
    queryFn: () => {
      const query = new URLSearchParams();

      if (activeBranchId) {
        query.set('branchId', String(activeBranchId));
      }

      if (activeYearId) {
        query.set('financialYearId', String(activeYearId));
      }

      return apiFetch<NumberingSeries[]>(`/api/document-numbering-series?${query.toString()}`);
    },
    enabled: activeBranchId !== undefined && activeYearId !== undefined,
  });

  const columns: DataViewColumn<NumberingSeries>[] = [
    {
      title: 'Document',
      dataIndex: 'documentType',
      primary: true,
      width: 200,
      render: (_, row) => (
        <span className="font-medium text-gray-900">{humanizeEnum(row.documentType)}</span>
      ),
    },
    {
      title: 'Prefix',
      dataIndex: 'prefix',
      width: 100,
      render: (_, row) => <span className="font-mono-nums">{row.prefix || '—'}</span>,
    },
    {
      title: 'Year in number',
      dataIndex: 'includeFinancialYear',
      width: 130,
      responsive: ['lg'],
      render: (_, row) => (
        <StatusBadge tone="neutral">{row.includeFinancialYear ? 'Yes' : 'No'}</StatusBadge>
      ),
    },
    {
      title: 'Padding',
      dataIndex: 'paddingWidth',
      width: 90,
      align: 'right',
      responsive: ['xl'],
      render: (_, row) => <span className="font-mono-nums">{row.paddingWidth}</span>,
    },
    {
      title: 'Issued',
      dataIndex: 'currentNumber',
      width: 90,
      align: 'right',
      render: (_, row) => <span className="font-mono-nums">{row.currentNumber}</span>,
    },
    {
      title: 'Next number',
      key: 'next',
      width: 190,
      render: (_, row) => (
        <span className="font-mono-nums font-semibold">
          {nextDocumentNumber(row, yearCode) ?? '—'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      width: 96,
      render: (_, row) =>
        row.isActive ? (
          <StatusBadge tone="positive">Active</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">Inactive</StatusBadge>
        ),
    },
  ];

  if (series.isError) {
    return <ApiErrorAlert error={series.error} />;
  }

  return (
    <>
      <PageHeading
        title="Document numbering"
        subtitle="How each document type is numbered, per branch and per financial year."
        actions={
          <>
            {/*
              The two filters are the screen's primary control, not a refinement of it — a series
              only means anything for one branch in one year — so they sit in the heading rather
              than inside the card with the results.
            */}
            <Select
              value={activeBranchId}
              onChange={setBranchId}
              className="w-48"
              loading={branches.isLoading}
              placeholder="Branch"
              options={(branches.data ?? []).map((branch) => ({
                label: `${branch.name} (${branch.code})`,
                value: branch.id,
              }))}
            />

            <Select
              value={activeYearId}
              onChange={setFinancialYearId}
              className="w-36"
              loading={years.isLoading}
              placeholder="Financial year"
              options={(years.data ?? []).map((year) => ({
                label: year.isCurrent ? `${year.code} (current)` : year.code,
                value: year.id,
              }))}
            />

            <WriteAction session={session} required="DocumentNumbering:Create">
              {(disabled) => (
                <Button
                  variant="primary"
                  icon="icon icon-plus"
                  shortcut="create"
                  disabled={disabled}
                  onClick={() => setCreating(true)}
                >
                  New series
                </Button>
              )}
            </WriteAction>
          </>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <DataView<NumberingSeries>
          columns={columns}
          dataSource={series.data ?? []}
          loading={series.isLoading || branches.isLoading || years.isLoading}
          empty={
            <EmptyState
              icon="icon icon-hash"
              title="No series for this branch and year"
              hint="A document type with no series cannot be numbered, so nothing of that type can be issued here."
            />
          }
        />
      </Card>

      {creating && (
        <SeriesDrawer
          defaults={{ branchId: activeBranchId, financialYearId: activeYearId }}
          yearCodeOf={(id) => years.data?.find((year) => year.id === id)?.code ?? null}
          branchOptions={(branches.data ?? []).map((branch) => ({
            label: `${branch.name} (${branch.code})`,
            value: branch.id,
          }))}
          yearOptions={(years.data ?? []).map((year) => ({ label: year.code, value: year.id }))}
          taken={(series.data ?? []).map((row) => row.documentType)}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

interface SeriesFormValues {
  branchId: number;
  financialYearId: number;
  documentType: DocumentType;
  prefix: string;
  includeFinancialYear: boolean;
  paddingWidth: number;
  separator: string;
}

/**
 * A new series, with the number it will produce shown as it is configured.
 *
 * The preview is the point of the drawer. Prefix, padding and separator are three fields whose
 * combined effect nobody predicts reliably in their head, and unlike most settings this one cannot
 * be quietly corrected later — documents already issued keep the format they were issued under.
 */
function SeriesDrawer({
  defaults,
  branchOptions,
  yearOptions,
  yearCodeOf,
  taken,
  onClose,
}: {
  defaults: { branchId?: number | undefined; financialYearId?: number | undefined };
  branchOptions: Array<{ label: string; value: number }>;
  yearOptions: Array<{ label: string; value: number }>;
  yearCodeOf: (id: number) => string | null;
  taken: DocumentType[];
  onClose: () => void;
}) {
  const [form] = Form.useForm<SeriesFormValues>();

  const create = useApiMutation<NumberingSeries, SeriesFormValues>({
    mutationFn: (values) =>
      apiFetch<NumberingSeries>('/api/document-numbering-series', {
        method: 'POST',
        body: values,
      }),
    success: 'Numbering series created.',
    invalidates: [seriesKey],
    onSuccess: onClose,
  });

  // One series per document type per branch per year — the server enforces it, and offering a
  // type that already has one only produces a conflict the user cannot act on.
  const available = DocumentTypeValues.filter((type) => !taken.includes(type));

  return (
    <FormDrawer<SeriesFormValues>
      title="New numbering series"
      open
      onClose={onClose}
      form={form}
      initialValues={{
        ...(defaults.branchId !== undefined ? { branchId: defaults.branchId } : {}),
        ...(defaults.financialYearId !== undefined
          ? { financialYearId: defaults.financialYearId }
          : {}),
        prefix: '',
        includeFinancialYear: true,
        paddingWidth: 4,
        separator: '-',
      }}
      onSubmit={(values) => void submitVia(create, values)}
      submitText="Create series"
      submitting={create.isPending}
    >
      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item
          name="branchId"
          label="Branch"
          rules={[{ required: true, message: 'Choose a branch' }]}
        >
          <Select options={branchOptions} />
        </Form.Item>

        <Form.Item
          name="financialYearId"
          label="Financial year"
          rules={[{ required: true, message: 'Choose a financial year' }]}
        >
          <Select options={yearOptions} />
        </Form.Item>
      </div>

      <Form.Item
        name="documentType"
        label="Document type"
        rules={[{ required: true, message: 'Choose a document type' }]}
      >
        <Select showSearch optionFilterProp="label" options={enumOptions(available)} />
      </Form.Item>

      <div className="grid grid-cols-3 gap-x-3">
        <Form.Item name="prefix" label="Prefix" extra="The leading token, e.g. CH.">
          <Input maxLength={10} className="font-mono-nums" />
        </Form.Item>

        <Form.Item
          name="separator"
          label="Separator"
          extra="Leave empty to run the segments together."
        >
          <Input maxLength={2} />
        </Form.Item>

        <Form.Item name="paddingWidth" label="Padding" extra="4 gives 0001; 0 does not pad.">
          <InputNumber min={0} max={12} precision={0} className="w-full" />
        </Form.Item>
      </div>

      <Form.Item
        name="includeFinancialYear"
        label="Include the financial year"
        valuePropName="checked"
        extra="Almost always on — it is what makes a number unique across years."
      >
        <Switch size="small" />
      </Form.Item>

      <NumberPreview form={form} yearCodeOf={yearCodeOf} />

      <div className="mt-4 rounded-md border border-border-color bg-light p-3">
        <p className="flex items-center gap-2 text-[13px] font-semibold text-gray-900">
          <i className="icon icon-info text-info" />
          The format is fixed once numbers are issued
        </p>
        <p className="mt-1 text-[13px] text-default">
          Documents keep the format they were issued under, so a series that has already been used
          cannot be tidied up retrospectively.
        </p>
      </div>
    </FormDrawer>
  );
}

/**
 * The live preview.
 *
 * `Form.useWatch` per field rather than one `shouldUpdate` re-render of the whole form: this is
 * ProComponents' `ProFormDependency` with the dependency list spelled out, and it re-renders only
 * this block rather than every field beside it.
 */
function NumberPreview({
  form,
  yearCodeOf,
}: {
  form: import('antd').FormInstance<SeriesFormValues>;
  yearCodeOf: (id: number) => string | null;
}) {
  const prefix = Form.useWatch('prefix', form);
  const separator = Form.useWatch('separator', form);
  const paddingWidth = Form.useWatch('paddingWidth', form);
  const includeFinancialYear = Form.useWatch('includeFinancialYear', form);
  const financialYearId = Form.useWatch('financialYearId', form);

  const preview = formatDocumentNumber(
    {
      prefix: String(prefix ?? ''),
      separator: String(separator ?? ''),
      paddingWidth: Number(paddingWidth ?? 0),
      includeFinancialYear: Boolean(includeFinancialYear),
    },
    typeof financialYearId === 'number' ? yearCodeOf(financialYearId) : null,
    1,
  );

  return (
    <div className="rounded-md border border-border-color bg-light px-3.5 py-3">
      <p className="text-[12px] text-default">The first document out of this series</p>
      <p className="font-mono-nums mt-1 text-[18px] font-semibold text-gray-900">
        {preview ?? '—'}
      </p>
    </div>
  );
}
